import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { GIFT_CARD_MAX_QUANTITY } from '../lib/rewardCatalogue';
import { getRewardImageCandidates, getRewardName } from '../lib/rewardImage';

/**
 * The gift-card basket.
 *
 * Deliberately separate from the product cart, and deliberately client-side.
 *
 *   separate   — `POST /api/cart/add` takes a `product_id` and nothing else. A
 *                reward is not a row in the products table and carries a chosen
 *                denomination, neither of which that endpoint can represent, so
 *                a reward pushed through it 404s.
 *   client-side — with no server representation there is nothing to sync
 *                against. The trade-off is real and worth stating: this basket
 *                does not follow the customer to another device or survive
 *                clearing site data. Vouchers are issued the instant payment
 *                clears, so nothing of value is held here — only an intent.
 *
 * A line is identified by reward *and* denomination: the same card at ₹500 and
 * at ₹1,000 is two lines, because they are two different things to issue.
 */

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

const STORAGE_KEY = 'gift_card_cart';

const GiftCardCartContext = createContext(null);

const lineIdFor = (rewardId, denomination) => `${rewardId}:${denomination}`;

/** Rejects anything that is not a well-formed line, so a hand-edited or
 *  half-written localStorage value cannot put NaN into a payable total. */
function sanitizeLines(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((line) => ({
      line_id: String(line?.line_id || lineIdFor(line?.reward_id, line?.denomination)),
      reward_id: String(line?.reward_id || ''),
      name: String(line?.name || 'Gift card'),
      denomination: Number(line?.denomination),
      quantity: Math.min(GIFT_CARD_MAX_QUANTITY, Math.max(1, Math.round(Number(line?.quantity) || 1))),
      image_candidates: Array.isArray(line?.image_candidates) ? line.image_candidates : [],
      markup_type: line?.markup_type ?? null,
      markup_value: Number.isFinite(Number(line?.markup_value)) ? Number(line.markup_value) : null,
    }))
    .filter((line) => line.reward_id && Number.isFinite(line.denomination) && line.denomination > 0);
}

function readStored() {
  try {
    return sanitizeLines(JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'));
  } catch {
    return [];
  }
}

export function GiftCardCartProvider({ children }) {
  const { user } = useAuth();
  
  // Determine storage key dynamically based on user
  const getStorageKey = useCallback(() => user ? `gift_card_cart_${user?.id || user?.user_id || user?.email}` : STORAGE_KEY, [user]);

  const [items, setItems] = useState(() => {
    try {
      return sanitizeLines(JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'));
    } catch {
      return [];
    }
  });

  // Handle cross-session / merge logic when user changes
  useEffect(() => {
    const key = getStorageKey();
    const storedUserCart = sanitizeLines(JSON.parse(localStorage.getItem(key) || '[]'));
    
    if (user) {
      // User just logged in: Check if they had a guest cart
      const guestCart = sanitizeLines(JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'));
      if (guestCart.length > 0) {
        // Merge guest cart into user cart
        const mergedMap = new Map();
        storedUserCart.forEach(item => mergedMap.set(item.line_id, item));
        guestCart.forEach(item => {
          if (mergedMap.has(item.line_id)) {
            const existing = mergedMap.get(item.line_id);
            existing.quantity = Math.min(GIFT_CARD_MAX_QUANTITY, existing.quantity + item.quantity);
          } else {
            mergedMap.set(item.line_id, item);
          }
        });
        const mergedItems = Array.from(mergedMap.values());
        setItems(mergedItems);
        localStorage.setItem(key, JSON.stringify(mergedItems));
        // We do not clear guest_cart here to avoid race conditions; AuthContext will clear it on logout
      } else {
        setItems(storedUserCart);
      }
    } else {
      // Guest or logged out
      setItems(storedUserCart);
    }
  }, [user, getStorageKey]);

  useEffect(() => {
    localStorage.setItem(getStorageKey(), JSON.stringify(items));
  }, [items, getStorageKey]);

  // Two tabs share one basket. Without this, adding a card in one tab and
  // checking out in the other silently drops the addition.
  useEffect(() => {
    const onStorage = (e) => { 
      if (e.key === getStorageKey()) {
        try {
          setItems(sanitizeLines(JSON.parse(localStorage.getItem(getStorageKey()) || '[]')));
        } catch {
          setItems([]);
        }
      } 
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [getStorageKey]);

  /**
   * Server-priced basket.
   *
   * The cart does not add anything up itself. `POST /giftcards/quote` runs the
   * same code path as `/purchase/bulk`, so the total on screen is the total that
   * will be charged by construction rather than by two implementations agreeing.
   * It also returns per-line availability, so a delisted card is marked in place
   * instead of failing the whole basket at checkout.
   *
   * Local sums stay as the fallback: a quote that cannot be fetched must not
   * blank a cart the customer can see.
   */
  const [quote, setQuote] = useState(null);
  const quoteSeq = useRef(0);

  useEffect(() => {
    if (!items.length) { setQuote(null); return; }

    const seq = ++quoteSeq.current;
    const token = localStorage.getItem('token');
    // Debounced: the quantity stepper fires per click and each one would
    // otherwise be a round-trip, with the replies free to arrive out of order.
    const timer = setTimeout(() => {
      fetch(`${API}/giftcards/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: 'include',
        body: JSON.stringify({
          items: items.map((line) => ({
            reward_id: line.reward_id,
            denomination: Math.round(line.denomination * 100),
            quantity: line.quantity,
          })),
        }),
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => { if (seq === quoteSeq.current) setQuote(data); })
        .catch(() => { /* keep the last good quote; the local sum covers us */ });
    }, 250);

    return () => clearTimeout(timer);
  }, [items]);

  /**
   * @param reward        the catalogue reward (or the product-page shape built
   *                      from one — both carry a name and image candidates)
   * @param denomination  the payable, marked-up rupee amount the customer chose
   * @returns {{added:number, capped:boolean}} so the caller can tell the
   *          customer when the provider's per-line cap swallowed part of the ask
   */
  const addGiftCard = useCallback((reward, denomination, quantity = 1) => {
    const rewardId = String(reward?.reward_id || reward?.id || '').replace(/^reward_/, '');
    const value = Number(denomination);
    const want = Math.max(1, Math.round(Number(quantity) || 1));

    if (!rewardId || !Number.isFinite(value) || value <= 0) {
      throw new Error('This gift card has no amount selected.');
    }

    const lineId = lineIdFor(rewardId, value);

    /*
      Computed from `items` and returned synchronously, not read back out of a
      setState updater. React runs an updater when it processes the update, which
      is after the event handler has finished — so a caller reading the result
      immediately got the value from before the change, and the "added 1 of 2,
      the rest hit the cap" toast could not have been right.
    */
    const existing = items.find((line) => line.line_id === lineId);
    const held = existing?.quantity || 0;
    const nextQty = Math.min(GIFT_CARD_MAX_QUANTITY, held + want);
    const result = { added: nextQty - held, capped: nextQty < held + want };

    setItems((prev) => {
      const current = prev.find((line) => line.line_id === lineId);
      const qty = Math.min(GIFT_CARD_MAX_QUANTITY, (current?.quantity || 0) + want);

      if (current) {
        return prev.map((line) => (line.line_id === lineId ? { ...line, quantity: qty } : line));
      }
      return [
        ...prev,
        {
          line_id: lineId,
          reward_id: rewardId,
          name: getRewardName(reward) || reward?.name || 'Gift card',
          denomination: value,
          quantity: qty,
          // Snapshotted, not re-fetched: the cart must render without walking
          // the 276-item catalogue again on every page load.
          //
          // Only the images are kept. This used to snapshot the entire catalogue
          // object — several KB per line — purely so the cart could hand it to
          // the product page, which had no other way to build a gift card. The
          // by-id lookup removed that need.
          image_candidates: getRewardImageCandidates(reward),
          // Snapshotted with the line so the cart can show the saving without
          // walking the catalogue again. The price is already discounted; these
          // only let the cart say by how much.
          markup_type: reward?.markup_type ?? null,
          markup_value: reward?.markup_value ?? null,
        },
      ];
    });

    return result;
  }, [items]);

  const setQuantity = useCallback((lineId, quantity) => {
    const next = Math.round(Number(quantity) || 0);
    setItems((prev) => (next <= 0
      ? prev.filter((line) => line.line_id !== lineId)
      : prev.map((line) => (line.line_id === lineId
        ? { ...line, quantity: Math.min(GIFT_CARD_MAX_QUANTITY, next) }
        : line))));
  }, []);

  const removeGiftCard = useCallback((lineId) => {
    setItems((prev) => prev.filter((line) => line.line_id !== lineId));
  }, []);

  const clearGiftCards = useCallback(() => setItems([]), []);

  const value = useMemo(() => {
    const count = items.reduce((sum, line) => sum + line.quantity, 0);
    // Local sum, used until the quote lands and whenever it cannot be fetched.
    const total = items.reduce((sum, line) => sum + line.denomination * line.quantity, 0);

    /*
      Keyed by reward + payable amount, which is exactly how a line is
      identified, so a row finds itself without the two arrays having to stay in
      the same order.

      `payable_amount` — the quote used to answer with `price` and the key was
      built from that; when the field was renamed every lookup silently returned
      nothing, and a cart falling back to its local sums looks fine right up
      until the totals matter.
    */
    const priced = quote
      ? new Map((quote.items || []).map((row) => [
        `${String(row.reward_id).replace(/^reward_/, '')}:${Number(row.payable_amount)}`,
        row,
      ]))
      : null;

    return {
      items,
      count,
      total,
      quote,
      lineQuote: (line) => priced?.get(line.line_id) || null,
      addGiftCard,
      setQuantity,
      removeGiftCard,
      clearGiftCards,
    };
  }, [items, quote, addGiftCard, setQuantity, removeGiftCard, clearGiftCards]);

  return <GiftCardCartContext.Provider value={value}>{children}</GiftCardCartContext.Provider>;
}

export function useGiftCardCart() {
  const ctx = useContext(GiftCardCartContext);
  if (!ctx) throw new Error('useGiftCardCart must be used inside GiftCardCartProvider');
  return ctx;
}
